import React, { useCallback, useState } from "react";
import { StandardPageWrapper } from "../../../components/layout/StandardPageWrapper";
import { Routes } from "../../../root/routes";
import { Breadcrumbs, Modal, SectionEditor, useSnackBar } from "@truevault/aquilo";
import {
  ProgressFooterSaveNotification,
  UseEditPrivacyCenter,
  useEditPrivacyCenter,
  useNoticeEditorMetadata,
  useUpdateNoticeApi,
} from "../../../components/privacyCenter/EditPrivacyCenter";
import { PrivacyCenterTabsNew } from "./PrivacyCenterTabsNew";
import { PrintPreviewPublishButtons } from "./us-state-notices/USStateNoticesPage";
import { usePrivacyCenterNotice } from "../../../hooks/usePrivacyCenterNotice";
import { usePrivacyCenterPreviewPopup } from "../StayCompliantPrivacyCenterPage";
import {
  PrivacyCenterPublishDatePickerModal,
  usePrivacyCenterPublish,
} from "../../../components/privacyCenter/PrivacyCenterPublish";
import { didFinishPoliciesAndNoticesOnboarding } from "../../../../common/hooks/useOnboardingProgress";
import moment from "moment";
import { DEVELOPER_INSTRUCTIONS_SURVEY_NAME } from "../PrivacyNoticeChecklistItems";
import { useSurvey } from "../../../../common/hooks/api/useSurvey";
import { PageLoading } from "../../../components";
import {
  NoticeEditorMetadataResponse,
  PrivacyCenterNoticeDto,
  PrivacyCenterNoticeSectionDto,
} from "../../../../common/service/server/dto/PrivacyCenterDto";
import { Form } from "react-final-form";
import _ from "lodash";
import { Redirect, useHistory } from "react-router";
import { ProgressFooter, ProgressFooterActions } from "../../get-compliant/ProgressFooter";

// Depending on if an organization has updated their Privacy Policy after certain Polaris updates, they may have various
// names for the GDPR section in the page. Need to be able to find it when rendering International Notices.
export const GDPR_SECTION_ANCHORS = ["eea-uk-privacy-notice", "eeaukch-gdpr", "eeauk-gdpr"];

export const PIPEDA_SECTION_ANCHORS = ["ca-pipeda-privacy-notice", "canada-pipeda"];

export function InternationalNoticesPage() {
  const history = useHistory();
  const editPrivacyCenter = useEditPrivacyCenter();

  const org = editPrivacyCenter?.props?.org;

  const { answers: developerInstructionsAnswers } = useSurvey(
    org?.id,
    DEVELOPER_INSTRUCTIONS_SURVEY_NAME,
  );
  const [draftNotice, , refreshDraftNotice] = usePrivacyCenterNotice(
    org?.id,
    editPrivacyCenter?.props?.privacyCenter?.id,
    editPrivacyCenter?.props?.draftPrivacyPolicyNoticeId,
  );
  const openPreviewNotice = usePrivacyCenterPreviewPopup();
  const { updateNotice } = useUpdateNoticeApi();
  const enqueueSnackbar = useSnackBar();
  const sendPublish = async () => {
    try {
      await updateNotice({
        organizationId: org?.id,
        privacyCenterId: draftNotice.privacyCenterId,
        noticeId: draftNotice.publicId,
        updateNoticeBody: {
          status: "PUBLISHED",
        },
      });

      await refreshDraftNotice();
      // refresh privacy center since privacyCenter.hiddenStateAnchors may have been invalidated
      await editPrivacyCenter?.props?.privacyCenterRefresh();
    } catch (error) {
      console.error("Failed to publish the notice:", error);
      enqueueSnackbar("Failed to publish. Please try again.", { variant: "error" });
    }
  };
  const [showPublishDateModal, setShowPublishDateModal] = useState(false);
  const { isDateSaving, onDateChange } = usePrivacyCenterPublish(
    org?.id,
    draftNotice,
    refreshDraftNotice,
  );

  const [isSaving, setIsSaving] = useState(false);
  const [isSaved, setIsSaved] = useState(false);

  const publishedAtNormalizedStr = draftNotice?.publishedAt?.replace(/-/g, "/");
  const publishedAtDate = moment(publishedAtNormalizedStr || new Date()).toDate();

  if (!org || !editPrivacyCenter || !developerInstructionsAnswers || !draftNotice) {
    return (
      <StandardPageWrapper>
        <PageLoading />
      </StandardPageWrapper>
    );
  }

  if (!org.featureGdpr && !org.featurePipeda) {
    const fallbackRoute = editPrivacyCenter.props.privacyCenter.hideCcpaSection
      ? Routes.privacyCenter.theme
      : Routes.privacyCenter.usStates;
    return <Redirect to={fallbackRoute} />;
  }

  const isAlreadyPublished = editPrivacyCenter?.props?.allNotices?.some(
    (notice) => notice.type === "PRIVACY_POLICY" && notice.status === "PUBLISHED",
  );

  // Show the first time user experience (e.g. modals, footer actions) if either (1) there isn't a published notice yet OR (2) the Policies & Notices onboarding steps are not completed
  const isFirstTimeOnboarding = !(
    isAlreadyPublished && didFinishPoliciesAndNoticesOnboarding(developerInstructionsAnswers)
  );

  const nextRoute = editPrivacyCenter.props.privacyCenter.hideCcpaSection
    ? Routes.privacyCenter.theme
    : Routes.privacyCenter.usStates;

  return (
    <StandardPageWrapper
      fullWidth
      footer={
        isFirstTimeOnboarding && (
          <ProgressFooter
            actions={
              <ProgressFooterActions
                nextContent="Done with International Notices"
                nextOnClick={() => history.push(nextRoute)}
                nextDisabled={false}
              />
            }
          />
        )
      }
    >
      <div>
        <div className="tw-boundary sticky-header">
          <Breadcrumbs title="International Notices" sourceRoute={Routes.privacyCenter.root} />

          <PrivacyCenterTabsNew
            org={editPrivacyCenter?.props?.org}
            selectedTabKey="international-notices"
            enableNextTab={true}
            enableAllTabs={true}
            trailingContent={
              <div className="flex items-center gap-2">
                <div className="flex text-neutral-600 font-normal text-left">
                  <ProgressFooterSaveNotification saving={isSaving} isSaved={isSaved} />
                </div>
                <PrintPreviewPublishButtons
                  orgId={org?.id}
                  privacyCenter={editPrivacyCenter?.props?.privacyCenter}
                  draftNoticePublicId={draftNotice.publicId}
                  hasPublishedNotice={
                    editPrivacyCenter?.props?.latestPublishedNoticeUpdatedOn != null
                  }
                  onPreviewClick={openPreviewNotice}
                  onPublishClick={() => setShowPublishDateModal(true)}
                  // In the onboarding/FTUE flow, we only enable the Publish button on the last step of the flow, so it should be disabled on this page if we're in FTUE
                  isPublishEnabled={
                    draftNotice?.sections.some(
                      (section) => !!section.header || !!section.content,
                    ) && didFinishPoliciesAndNoticesOnboarding(developerInstructionsAnswers)
                  }
                  publishDisabledTooltip={
                    !didFinishPoliciesAndNoticesOnboarding(developerInstructionsAnswers)
                      ? "Complete the Themes step to publish your Privacy Center."
                      : undefined
                  }
                />
              </div>
            }
          />
        </div>

        <div style={{ marginTop: "24px" }}>
          <InternationalNoticesEditors
            editPrivacyCenter={editPrivacyCenter}
            draftNotice={draftNotice}
            refreshDraftNotice={refreshDraftNotice}
            setIsSaving={setIsSaving}
            setIsSaved={setIsSaved}
          />
        </div>

        {showPublishDateModal && (
          <PrivacyCenterPublishDatePickerModal
            onPublishClick={sendPublish}
            closeModal={() => setShowPublishDateModal(false)}
            publishedAtDate={publishedAtDate}
            onDateChange={onDateChange}
            disabled={isDateSaving}
          />
        )}
      </div>
    </StandardPageWrapper>
  );
}

function InternationalNoticesEditors({
  editPrivacyCenter,
  draftNotice,
  refreshDraftNotice,
  setIsSaving,
  setIsSaved,
}: {
  editPrivacyCenter: UseEditPrivacyCenter;
  draftNotice: PrivacyCenterNoticeDto;
  refreshDraftNotice: () => void;
  setIsSaving: (saving: boolean) => void;
  setIsSaved: (isSaved: boolean) => void;
}) {
  let internationalSections =
    draftNotice && draftNotice.sections
      ? [
          // Make sure GDPR sections appear before PIPEDA sections in the list
          ...draftNotice.sections.filter((section) =>
            GDPR_SECTION_ANCHORS.includes(section.anchor),
          ),
          ...draftNotice.sections.filter((section) =>
            PIPEDA_SECTION_ANCHORS.includes(section.anchor),
          ),
        ]
      : [];

  const [noticeEditorMetadata] = useNoticeEditorMetadata(
    editPrivacyCenter?.props?.org?.id,
    editPrivacyCenter?.props?.privacyCenter?.id,
  );

  return (
    <div>
      <Form
        onSubmit={() => {}}
        render={(_formRenderProps) => (
          <div>
            {internationalSections.map((section) => (
              <div key={section.id}>
                <InternationalSectionEditor
                  organizationId={editPrivacyCenter?.props?.org?.id}
                  privacyCenterId={editPrivacyCenter?.props?.privacyCenter?.id}
                  noticeId={draftNotice.id}
                  noticePublicId={draftNotice.publicId}
                  refreshNotice={refreshDraftNotice}
                  noticeEditorMetadata={noticeEditorMetadata}
                  section={section}
                  setIsSaving={setIsSaving}
                  setIsSaved={setIsSaved}
                />
              </div>
            ))}
          </div>
        )}
      />
    </div>
  );
}

function InternationalSectionEditor({
  organizationId,
  privacyCenterId,
  noticeId,
  noticePublicId,
  section,
  noticeEditorMetadata,
  refreshNotice,
  setIsSaving,
  setIsSaved,
}: {
  organizationId: string;
  privacyCenterId: string;
  noticeId: string;
  noticePublicId: string;
  section: PrivacyCenterNoticeSectionDto;
  noticeEditorMetadata?: NoticeEditorMetadataResponse;
  refreshNotice: () => void;
  setIsSaving: (saving: boolean) => void;
  setIsSaved: (isSaved: boolean) => void;
}) {
  // Disclaimer modal that appears when unlocking the GDPR section
  const [showGdprUnlockDisclaimerModal, setShowGdprUnlockDisclaimerModal] = useState(false);
  const [gdprSectionIdToUnlock, setGdprSectionIdToUnlock] = useState<string | null>(null);
  // Disclaimer modal that appears when unlocking the PIPEDA section
  const [showPipedaUnlockDisclaimerModal, setShowPipedaUnlockDisclaimerModal] = useState(false);
  const [pipedaSectionIdToUnlock, setPipedaSectionIdToUnlock] = useState<string | null>(null);

  const { updateNotice } = useUpdateNoticeApi();

  const debouncedSave = useCallback(
    _.debounce(async (newContent: string) => {
      setIsSaving(true);

      let updateNoticeBody = {
        organizationId: organizationId,
        privacyCenterId: privacyCenterId,
        noticeId: noticePublicId,
        updateNoticeBody: {
          id: noticeId,
          sections: [
            {
              id: section.id,
              content: newContent,
            },
          ],
        },
      };

      await updateNotice(updateNoticeBody);

      setIsSaving(false);
      setIsSaved(true);
      setTimeout(() => setIsSaved(false), 2000);

      await refreshNotice();
    }, 750),
    [],
  );

  return (
    <div
      id={`section-${section.anchor}`}
      className="individual-section-wrapper"
      style={{ marginTop: "48px", width: "100%" }}
    >
      <SectionEditor
        // Use a key that is based on the hidden and editable state of the section
        // This works around an issue where the Redactor X editor doesn't become editable without the entire component re-rendering, so using a key that changes when the section becomes editable forces the component to re-render
        key={`${section.anchor}-${section.locked}`}
        addToNavigation={section.addToNavigation}
        sectionId={section.id}
        header={section.header}
        content={section.content}
        placeholder={section.placeholder}
        locked={section.locked}
        enablePrivacyCenterToolbar={true}
        availablePrivacyCenterEmbeddedAssets={
          new Map(Object.entries(noticeEditorMetadata?.availableEmbeddedAssets || {}))
        }
        onInputChange={({ content }) => {
          if (!section.locked) {
            debouncedSave(content);
          }
        }}
        onUnlock={async () => {
          if (GDPR_SECTION_ANCHORS.includes(section.anchor)) {
            setShowGdprUnlockDisclaimerModal(true);
            setGdprSectionIdToUnlock(section.id);
          } else if (PIPEDA_SECTION_ANCHORS.includes(section.anchor)) {
            setShowPipedaUnlockDisclaimerModal(true);
            setPipedaSectionIdToUnlock(section.id);
          } else {
            await updateNotice({
              organizationId: organizationId,
              privacyCenterId: privacyCenterId,
              noticeId: noticePublicId,
              updateNoticeBody: {
                sections: [
                  {
                    id: section.id,
                    locked: !section.locked,
                  },
                ],
              },
            });
            await refreshNotice();
          }
        }}
        onShowInNavigationCheckboxToggle={async () => {
          await updateNotice({
            organizationId: organizationId,
            privacyCenterId: privacyCenterId,
            noticeId: noticePublicId,
            updateNoticeBody: {
              sections: [
                {
                  id: section.id,
                  addToNavigation: !section.addToNavigation,
                },
              ],
            },
          });
          await refreshNotice();
        }}
      />
      {showGdprUnlockDisclaimerModal && (
        <Modal
          title="You are about to alter the EEA/UK/CH GDPR Notice"
          alignText="center"
          firstButton="Cancel"
          firstButtonColor="tertiary"
          onFirstButtonClick={() => {
            setShowGdprUnlockDisclaimerModal(false);
            setGdprSectionIdToUnlock(null);
          }}
          secondButton="I Understand"
          secondButtonColor="warning"
          onSecondButtonClick={async () => {
            await updateNotice({
              organizationId: organizationId,
              privacyCenterId: privacyCenterId,
              noticeId: noticePublicId,
              updateNoticeBody: {
                sections: [
                  {
                    id: gdprSectionIdToUnlock,
                    locked: false,
                  },
                ],
              },
            });
            await refreshNotice();
            setShowGdprUnlockDisclaimerModal(false);
            setGdprSectionIdToUnlock(null);
          }}
          showOverlay
          onClose={() => {
            setShowGdprUnlockDisclaimerModal(false);
            setGdprSectionIdToUnlock(null);
          }}
          widthButton="full"
          alignButton="right"
        >
          <p className="text-center text-gray-500">
            Our notice templates have been thoroughly researched and reviewed for compliance and get
            regularly updated with new regulations and changes to your Data Map.
          </p>
          <p className="text-center text-gray-500" style={{ marginTop: "35px" }}>
            By unlocking and changing the notice you assume responsibility for its content. You can
            add the GDPR Notice template at any time.
          </p>
        </Modal>
      )}
      {showPipedaUnlockDisclaimerModal && (
        <Modal
          title="You are about to alter the Canada PIPEDA Notice"
          alignText="center"
          firstButton="Cancel"
          firstButtonColor="tertiary"
          onFirstButtonClick={() => {
            setShowPipedaUnlockDisclaimerModal(false);
            setPipedaSectionIdToUnlock(null);
          }}
          secondButton="I Understand"
          secondButtonColor="warning"
          onSecondButtonClick={async () => {
            await updateNotice({
              organizationId: organizationId,
              privacyCenterId: privacyCenterId,
              noticeId: noticePublicId,
              updateNoticeBody: {
                sections: [
                  {
                    id: pipedaSectionIdToUnlock,
                    locked: false,
                  },
                ],
              },
            });
            await refreshNotice();
            setShowPipedaUnlockDisclaimerModal(false);
            setPipedaSectionIdToUnlock(null);
          }}
          showOverlay
          onClose={() => {
            setShowPipedaUnlockDisclaimerModal(false);
            setPipedaSectionIdToUnlock(null);
          }}
          widthButton="full"
          alignButton="right"
        >
          <p className="text-center text-gray-500">
            Our notice templates have been thoroughly researched and reviewed for compliance and get
            regularly updated with new regulations and changes to your Data Map.
          </p>
          <p className="text-center text-gray-500" style={{ marginTop: "35px" }}>
            By unlocking and changing the notice you assume responsibility for its content. You can
            restore the PIPEDA Notice template at any time.
          </p>
        </Modal>
      )}
    </div>
  );
}
